import React, { useEffect, useMemo, useRef, useState } from 'react';

import history from '../../../utils/history';
import ChapterSelector from '../../Common/ChapterSelector/ChapterSelector';
import CircularLoading from '../../Common/CircularLoading/CircularLoading';
import ModalBase from '../../ModalBase/ModalBase';

const MIN_QUESTIONS = 175;
const MODAL_CASES = {
  LOADING: 'loading',
  AVAILABLE: 'available',
  WAIT24H: 'wait24hours',
  RUNNING: 'running',
  READY: 'ready',
  NOT_ENOUGH: 'notEnoughQuestions',
};

function ChapterCustomExamPopUp({
  isOpen,
  toggle,
  chapterSelection,
  isSaving,
  saveSelectedChapters,
  user,
  customPracticeExamStatus,
  checkCustomPracticeExamStatus,
  loadCustomExamRefs,
  getCustomPracticeExamRef,
  getCustomPracticeExamRefLoading,
  generateCustomPE,
}) {
  const chapterIds = useMemo(
    () =>
      chapterSelection?.flatMap((item) =>
        item.chapters.map((chapter) => chapter.id),
      ) ?? [],
    [chapterSelection],
  );

  const [selectedChapterIds, setSelectedChapterIds] = useState(new Set());
  const [readyToGenerate, setReadyToGenerate] = useState(false);
  const [initialCheckDone, setInitialCheckDone] = useState(false);
  const refsLoaded = useRef(false);

  useEffect(() => {
    checkCustomPracticeExamStatus();
  }, []);

  useEffect(() => {
    if (refsLoaded.current) return;
    if (user?.rate?.id && customPracticeExamStatus) {
      const { practiceExamRunning, availablePracticeExam, has24HoursPassed } =
        customPracticeExamStatus;
      // canGenerate means we have the ability to create a new custom exam (cases READY or NOT_ENOUGH (not enough questiosn)),
      // we need to check first if the questions within PS selecteed chapters are enough
      const canGenerate =
        !availablePracticeExam &&
        has24HoursPassed !== false &&
        !practiceExamRunning?.isCustom;
      if (canGenerate) {
        loadCustomExamRefs(user.rate.id);
        refsLoaded.current = true;
      }
    }
  }, [user, customPracticeExamStatus]);

  useEffect(() => {
    if (isOpen) {
      setReadyToGenerate(false);
      setInitialCheckDone(false);
      setSelectedChapterIds(new Set(chapterIds));
    }
  }, [isOpen]);

  const chapterQuestionCount = useMemo(() => {
    const map = {};
    getCustomPracticeExamRef?.forEach((ref) => {
      ref.chapters?.forEach((ch) => {
        map[ch.id] = ch.questionCount || 0;
      });
    });
    return map;
  }, [getCustomPracticeExamRef]);

  const totalQuestionCount = useMemo(() => {
    let total = 0;
    selectedChapterIds.forEach((id) => {
      total += chapterQuestionCount[id] || 0;
    });
    return total;
  }, [selectedChapterIds, chapterQuestionCount]);

  useEffect(() => {
    // this is needed to verify chapters selection from personalized study have enough questions
    if (
      !initialCheckDone &&
      getCustomPracticeExamRef &&
      totalQuestionCount > 0
    ) {
      if (totalQuestionCount >= MIN_QUESTIONS) {
        setReadyToGenerate(true);
      }
      setInitialCheckDone(true);
    }
  }, [totalQuestionCount, getCustomPracticeExamRef, initialCheckDone]);

  const adaptedReferences = useMemo(
    () =>
      getCustomPracticeExamRef?.map((ref) => ({
        referenceId: ref.id,
        referenceName: ref.name,
        chapters: ref.chapters,
      })) ?? [],
    [getCustomPracticeExamRef],
  );

  const handleSelectionChange = (newSet) => {
    setSelectedChapterIds(newSet);
  };

  const handleSaveChapters = () => {
    saveSelectedChapters(Array.from(selectedChapterIds));
    setReadyToGenerate(true);
  };
  const handleGoToPracticeExam = () => {
    toggle();
    window.localStorage.setItem('fromPSView', 'true');
    history.push({ pathname: '/app/exams' });
  };
  const handleGenerate = () => {
    generateCustomPE([...selectedChapterIds]);
    handleGoToPracticeExam();
  };

  const getModalCase = () => {
    if (!customPracticeExamStatus || getCustomPracticeExamRefLoading)
      return MODAL_CASES.LOADING;
    const { practiceExamRunning, availablePracticeExam, has24HoursPassed } =
      customPracticeExamStatus;
    if (availablePracticeExam) return MODAL_CASES.AVAILABLE;
    if (has24HoursPassed === false) return MODAL_CASES.WAIT24H;
    if (practiceExamRunning?.isCustom) return MODAL_CASES.RUNNING;
    if (readyToGenerate) return MODAL_CASES.READY;
    return MODAL_CASES.NOT_ENOUGH;
  };

  const renderBody = () => {
    switch (getModalCase()) {
      case MODAL_CASES.LOADING:
        return <CircularLoading />;
      case MODAL_CASES.WAIT24H:
        return (
          <p>
            It hasn&apos;t been 24 hours since you last took a custom practice
            exam. Check back later to generate a new one.
          </p>
        );
      case MODAL_CASES.RUNNING:
        return <p>A custom practice exam is currently in progress!</p>;
      case MODAL_CASES.AVAILABLE:
        return <p>You have a custom practice exam ready to take</p>;
      case MODAL_CASES.READY:
        return (
          <p>
            You&apos;re about to generate a custom practice exam with randomly
            selected questions from your chosen chapters. Ready to continue?
          </p>
        );
      case MODAL_CASES.NOT_ENOUGH:
      default:
        return (
          <>
            {getCustomPracticeExamRefLoading ? (
              <CircularLoading />
            ) : (
              <>
                <p>
                  <strong>
                    You don&apos;t have enough questions to generate a custom
                    practice exam.
                  </strong>{' '}
                  Add more chapters to your study list.
                </p>
                <ChapterSelector
                  references={adaptedReferences}
                  selectedChapterIds={selectedChapterIds}
                  onSelectionChange={handleSelectionChange}
                />
                <div className="custom-exam-question-count">
                  <span
                    className={
                      totalQuestionCount >= MIN_QUESTIONS
                        ? 'enough'
                        : 'not-enough'
                    }
                  >
                    {totalQuestionCount} questions available from selected
                    chapters
                    {totalQuestionCount < MIN_QUESTIONS &&
                      `, you need at least ${MIN_QUESTIONS}`}
                  </span>
                </div>
              </>
            )}
          </>
        );
    }
  };

  return (
    <ModalBase
      isOpen={isOpen}
      toggle={toggle}
      header="Personalized Study Automatic Exam"
      body={renderBody()}
      footer={
        <>
          <button className="modal-base-btn secondary" onClick={toggle}>
            Cancel
          </button>
          {getModalCase() === MODAL_CASES.READY && (
            <button
              className="modal-base-btn primary"
              onClick={handleGenerate}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Generate'}
            </button>
          )}
          {getModalCase() === MODAL_CASES.NOT_ENOUGH && (
            <button
              className="modal-base-btn primary"
              onClick={handleSaveChapters}
              disabled={totalQuestionCount < MIN_QUESTIONS || isSaving}
            >
              {isSaving ? 'Saving...' : 'Save chapters'}
            </button>
          )}
          {getModalCase() !== MODAL_CASES.NOT_ENOUGH &&
            getModalCase() !== MODAL_CASES.LOADING &&
            getModalCase() !== MODAL_CASES.READY && (
              <button
                className="modal-base-btn primary"
                onClick={handleGoToPracticeExam}
              >
                Go to practice exam
              </button>
            )}
        </>
      }
    />
  );
}

export default ChapterCustomExamPopUp;
